import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Building2, Loader2, AlertCircle,
    RotateCcw, Send, ChevronDown, IndianRupee,
    Hash, CheckCircle, ArrowLeftRight,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    formatIndianCurrency,
    getAmountDisplay,
} from '../../utilities/amountToTextHelper';

import {
    submitBankTransfer,
    clearSaveResult,
    resetBankTransfer,
    selectBtSaveStatus,
    selectBtLoading,
    selectBtError,
} from '../../slices/accountsSlice/bankTransferSlice';

import {
    fetchBankDetailsWithAvailableBalance,
    selectBankDetailsArray,
    selectBankDetailsLoading,
} from '../../slices/CommonSlice/bankDetailsSlice';

import {
    fetchChequeNumbers,
    selectChequeNumbersArray,
    selectChequeNumbersLoading,
    resetChequeNumbersData,
} from '../../slices/bankSlice/chequeNumbersSlice';


// Date picker values are JS Dates; sent raw they serialise as UTC ("…T18:30:00Z" = previous day in IST).
// Send the local calendar date instead; strings (already formatted) pass through unchanged.
const toApiDate = (v) => {
    if (!v) return '';
    if (v instanceof Date) {
        return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
    }
    return v;
};

// ── Constants ─────────────────────────────────────────────────────────────────

// Legacy pay-mode list for this screen (value is stored as BankTransactions.ModeOfPay)
const MODE_OPTIONS = ['Cheque', 'Cash', 'RTGS/E-Trans', 'DD'];

// ── Shared styles (compact) ───────────────────────────────────────────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const CardHeader = ({ icon: Icon, title, action }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-indigo-500" />
            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
        </div>
        {action}
    </div>
);

// Summary panel row
const SumRow = ({ label, value }) => (
    <div className="flex justify-between gap-3">
        <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</dt>
        <dd className="font-semibold text-gray-800 dark:text-gray-100 truncate text-right" title={value || ''}>{value || '—'}</dd>
    </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const BankTransfer = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const roleId   = userData?.roleId   || userData?.RID  || 0;
    const userId   = userData?.userId   || userData?.UID  || userData?.employeeId || '';
    const userName = userData?.userName || userData?.UserName || 'system';

    // ── Redux ──────────────────────────────────────────────────────────────────
    const bankList      = useSelector(selectBankDetailsArray);
    const bankLoading   = useSelector(selectBankDetailsLoading);
    const chequeList    = useSelector(selectChequeNumbersArray);
    const chequeLoading = useSelector(selectChequeNumbersLoading);
    const saveStatus    = useSelector(selectBtSaveStatus);
    const saveLoading   = useSelector(selectBtLoading);
    const saveError     = useSelector(selectBtError);

    // ── Form state ─────────────────────────────────────────────────────────────
    const [fromBank,  setFromBank]  = useState(null);
    const [toBank,    setToBank]    = useState(null);
    const [modeOfPay, setModeOfPay] = useState('');
    const [chequeId,  setChequeId]  = useState('');
    const [refNo,     setRefNo]     = useState('');
    const [date,      setDate]      = useState(null);
    const [remarks,   setRemarks]   = useState('');
    const [amount,    setAmount]    = useState('');

    // ── Init ───────────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchBankDetailsWithAvailableBalance());
        return () => {
            dispatch(resetBankTransfer());
            dispatch(resetChequeNumbersData());
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // ── From-bank change: fetch cheques, clear downstream ─────────────────────
    useEffect(() => {
        if (!fromBank) { dispatch(resetChequeNumbersData()); return; }
        setModeOfPay('');
        setChequeId('');
        setRefNo('');
        // Reset To bank if same as From
        if (toBank?.BankId === fromBank.BankId) setToBank(null);
        dispatch(fetchChequeNumbers(fromBank.BankName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromBank]);

    // ── Mode change: clear ref fields ─────────────────────────────────────────
    useEffect(() => {
        setChequeId('');
        setRefNo('');
    }, [modeOfPay]);

    // ── Save result ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (saveStatus === 'success') {
            toast.success('Bank transfer saved successfully!');
            dispatch(clearSaveResult());
            handleReset();
        } else if (saveStatus === 'failed' && saveError) {
            toast.error(saveError);
            dispatch(clearSaveResult());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus, saveError]);

    // ── Handlers ───────────────────────────────────────────────────────────────
    const handleReset = () => {
        setFromBank(null);
        setToBank(null);
        setModeOfPay('');
        setChequeId('');
        setRefNo('');
        setDate(null);
        setRemarks('');
        setAmount('');
        dispatch(resetChequeNumbersData());
    };

    const handleAmountChange = (e) => {
        const val = e.target.value;
        if (val === '' || /^\d*\.?\d*$/.test(val)) setAmount(val);
    };

    const handleSubmit = () => {
        if (!fromBank)  { toast.warn('Please select the source bank account.');       return; }
        if (!toBank)    { toast.warn('Please select the destination bank account.');  return; }
        if (fromBank.BankId === toBank.BankId) {
            toast.warn('Source and destination banks must be different.'); return;
        }
        if (!modeOfPay) { toast.warn('Please select mode of payment.');               return; }
        if (modeOfPay === 'Cheque' && !chequeId) {
            toast.warn('Please select a cheque number.'); return;
        }
        if (modeOfPay !== 'Cheque' && !refNo.trim()) {
            toast.warn('Please enter the reference / transaction number.'); return;
        }
        if (!date)   { toast.warn('Please select a transfer date.');                  return; }
        if (!amount || parseFloat(amount) <= 0) {
            toast.warn('Please enter a valid amount.'); return;
        }
        if (!remarks.trim()) { toast.warn('Please enter remarks.');                   return; }

        const numAmount = parseFloat(amount);
        const available = parseFloat(fromBank.AvailableBalance) || 0;
        if (numAmount > available) {
            toast.warn(`Amount ₹${formatIndianCurrency(numAmount)} exceeds available balance ₹${formatIndianCurrency(available)}.`);
            return;
        }

        dispatch(submitBankTransfer({
            From:        fromBank.BankId,
            To:          toBank.BankId,
            Mode_of_Pay: modeOfPay,
            ...(modeOfPay === 'Cheque'
                ? { Chequeid: chequeId }
                : { Chequeid: '0', No: refNo }),
            Date:        toApiDate(date),
            Remarks:     remarks,
            Amount:      numAmount,
            RoleID:      roleId,
            Createdby:   userName,
        }));
    };

    // ── Derived ────────────────────────────────────────────────────────────────
    const numAmount      = parseFloat(amount) || 0;
    const available      = parseFloat(fromBank?.AvailableBalance) || 0;
    const exceedsBalance = fromBank && numAmount > 0 && numAmount > available;
    const amountDisplay  = getAmountDisplay(numAmount);
    const selectedCheqNo = chequeList.find(c => String(c.Cheque_Id) === String(chequeId))?.Cheque_No || '—';
    const refDisplay     = modeOfPay === 'Cheque' ? selectedCheqNo : (refNo || '—');

    // To-bank list excludes the selected From bank
    const toBankList = bankList.filter(b => b.BankId !== fromBank?.BankId);

    const showSummary = fromBank && toBank && modeOfPay &&
        (modeOfPay === 'Cheque' ? !!chequeId : !!refNo) &&
        date && numAmount > 0;

    const canSubmit = showSummary && !exceedsBalance && !saveLoading && remarks.trim();
    const isBusy    = saveLoading || bankLoading;

    const dateDisplay = date
        ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')
        : '—';

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* ── Page Header (slim) ───────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <ArrowLeftRight className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                            {menuData?.name || 'Bank to Bank Transfer'}
                        </h1>
                        <p className="text-indigo-200 text-xs">Accounts Module · Transfer funds between bank accounts</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

                {/* ── Left: single form card ───────────────────────────────── */}
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <CardHeader icon={Building2} title="Transfer Details" />
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">

                        {/* From Bank */}
                        <div>
                            <Label required>From Bank (Source)</Label>
                            <div className="relative">
                                <select
                                    className={`${inputCls} appearance-none pr-8`}
                                    value={fromBank?.BankId || ''}
                                    onChange={(e) => {
                                        const bank = bankList.find(b => b.BankId === parseInt(e.target.value));
                                        setFromBank(bank || null);
                                    }}
                                    disabled={bankLoading || isBusy}
                                >
                                    <option value="">{bankLoading ? 'Loading…' : '— Select Source Bank —'}</option>
                                    {bankList.map((b) => (
                                        <option key={b.BankId} value={b.BankId}>{b.BankName} — {b.AccountNo}</option>
                                    ))}
                                </select>
                                <SelectIcon loading={bankLoading} />
                            </div>
                            {fromBank && (
                                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 truncate"
                                    title={`${fromBank.AccountType || ''} · ${fromBank.AccountHolderName || ''} · Actual ₹ ${formatIndianCurrency(fromBank.ActualBalance)}`}>
                                    Actual ₹ {formatIndianCurrency(fromBank.ActualBalance)} ·{' '}
                                    <span className="font-semibold text-indigo-700 dark:text-indigo-300">Available ₹ {formatIndianCurrency(fromBank.AvailableBalance)}</span>
                                </p>
                            )}
                        </div>

                        {/* To Bank */}
                        <div>
                            <Label required>To Bank (Destination)</Label>
                            <div className="relative">
                                <select
                                    className={`${inputCls} appearance-none pr-8`}
                                    value={toBank?.BankId || ''}
                                    onChange={(e) => {
                                        const bank = toBankList.find(b => b.BankId === parseInt(e.target.value));
                                        setToBank(bank || null);
                                    }}
                                    disabled={bankLoading || isBusy || !fromBank}
                                >
                                    <option value="">{!fromBank ? 'Select source bank first' : bankLoading ? 'Loading…' : '— Select Destination Bank —'}</option>
                                    {toBankList.map((b) => (
                                        <option key={b.BankId} value={b.BankId}>{b.BankName} — {b.AccountNo}</option>
                                    ))}
                                </select>
                                <SelectIcon loading={bankLoading} />
                            </div>
                        </div>

                        {/* Mode of Payment */}
                        <div>
                            <Label required>Mode of Payment</Label>
                            <div className="relative">
                                <select
                                    className={`${inputCls} appearance-none pr-8`}
                                    value={modeOfPay}
                                    onChange={(e) => setModeOfPay(e.target.value)}
                                    disabled={!fromBank || isBusy}
                                >
                                    <option value="">{!fromBank ? 'Select source bank first' : '— Select Mode —'}</option>
                                    {MODE_OPTIONS.map((m) => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                </select>
                                <SelectIcon loading={false} />
                            </div>
                        </div>

                        {/* Cheque Number / Reference No */}
                        {modeOfPay === 'Cheque' ? (
                            <div>
                                <Label required>Cheque Number</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-8`}
                                        value={chequeId}
                                        onChange={(e) => setChequeId(e.target.value)}
                                        disabled={chequeLoading || isBusy}
                                    >
                                        <option value="">{chequeLoading ? 'Loading…' : '— Select Cheque —'}</option>
                                        {chequeList.map((c) => (
                                            <option key={c.Cheque_Id} value={c.Cheque_Id}>{c.Cheque_No}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={chequeLoading} />
                                </div>
                            </div>
                        ) : (
                            <div>
                                <Label required>Transaction / Ref No.</Label>
                                <div className="relative">
                                    <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                    <input
                                        type="text"
                                        className={`${inputCls} pl-8`}
                                        placeholder={modeOfPay ? 'Enter reference number' : 'Select mode first'}
                                        value={refNo}
                                        onChange={(e) => setRefNo(e.target.value)}
                                        disabled={!modeOfPay || isBusy}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Date */}
                        <div>
                            <Label required>Transfer Date</Label>
                            <CustomDatePicker
                                value={date}
                                onChange={setDate}
                                disabled={isBusy}
                                maxDate={new Date()}
                            />
                        </div>

                        {/* Amount */}
                        <div>
                            <Label required>Transfer Amount (₹)</Label>
                            <div className="relative">
                                <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    value={amount}
                                    onChange={handleAmountChange}
                                    disabled={!fromBank || isBusy}
                                    className={`${inputCls} pl-8 ${exceedsBalance ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-100' : ''}`}
                                />
                            </div>
                            {exceedsBalance && (
                                <p className="text-[11px] text-rose-500 mt-1 flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" /> Exceeds available ₹{formatIndianCurrency(available)}
                                </p>
                            )}
                        </div>

                        {/* Remarks */}
                        <div className="sm:col-span-2 lg:col-span-3">
                            <Label required>Remarks</Label>
                            <textarea
                                rows={1}
                                placeholder="Describe the purpose of this transfer…"
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                disabled={isBusy}
                                className={`${inputCls} resize-y`}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Right: sticky summary + actions ──────────────────────── */}
                <div className="lg:sticky lg:top-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={CheckCircle} title="Transfer Summary" />
                        <div className="p-4 space-y-3">
                            {fromBank && toBank && (
                                <div className="flex items-center gap-2 p-2 rounded-lg bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-100 dark:border-indigo-800">
                                    <div className="flex-1 min-w-0 text-center">
                                        <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 truncate" title={fromBank.BankName}>{fromBank.BankName}</p>
                                        <p className="text-[10px] text-gray-400 truncate">{fromBank.AccountNo}</p>
                                    </div>
                                    <ArrowLeftRight className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                                    <div className="flex-1 min-w-0 text-center">
                                        <p className="text-xs font-bold text-purple-600 dark:text-purple-400 truncate" title={toBank.BankName}>{toBank.BankName}</p>
                                        <p className="text-[10px] text-gray-400 truncate">{toBank.AccountNo}</p>
                                    </div>
                                </div>
                            )}
                            <dl className="space-y-1.5 text-xs">
                                <SumRow label="From Bank"  value={fromBank ? `${fromBank.BankName} — ${fromBank.AccountNo}` : ''} />
                                <SumRow label="Available"  value={fromBank ? `₹ ${formatIndianCurrency(fromBank.AvailableBalance)}` : ''} />
                                <SumRow label="To Bank"    value={toBank ? `${toBank.BankName} — ${toBank.AccountNo}` : ''} />
                                <SumRow label="Mode"       value={modeOfPay} />
                                <SumRow label={modeOfPay === 'Cheque' ? 'Cheque No' : 'Reference No'} value={refDisplay === '—' ? '' : refDisplay} />
                                <SumRow label="Date"       value={date ? dateDisplay : ''} />
                                <SumRow label="Created By" value={userName} />
                            </dl>

                            <div className={`px-4 py-3 rounded-lg text-white bg-gradient-to-r ${exceedsBalance ? 'from-rose-500 to-rose-600' : 'from-indigo-500 to-violet-600'}`}>
                                <p className="text-white/80 text-[10px] font-semibold uppercase tracking-wider">Transfer Amount</p>
                                <p className="text-2xl font-black">₹ {formatIndianCurrency(numAmount)}</p>
                                {exceedsBalance
                                    ? <p className="text-[11px] text-white/90">Exceeds available balance</p>
                                    : numAmount > 0 && <p className="text-[11px] text-white/80 italic leading-snug">{amountDisplay.words}</p>}
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={handleReset}
                                    disabled={isBusy}
                                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                                >
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={!canSubmit}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    {saveLoading
                                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                                        : <><Send className="h-4 w-4" /> Submit Transfer</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BankTransfer;
